import React, { useState, useEffect, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Menu as MenuIcon, X as CloseIcon } from 'lucide-react';
import luckblock from '../../assets/images/luckblocks.png';
import twitter from '../../assets/socialIcons/twitter.png';
import telegram from '../../assets/socialIcons/telegram.png';
import LanguageSwitcher from '../LanguageSwitcher';
import { useI18n } from '../../i18n';

function FetchConnectionButton() {
  const { t } = useI18n();
  return (
    <appkit-button
      balance="show"
      label={t('common.connectWallet')}
      loadingLabel={t('common.loading')}
    />
  );
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

/* `name` holds a translation key; coin tickers use `raw` and stay literal. */
const menuItems = [
  {
    name: 'nav.raffle',
    isDropdown: true,
    match: ['/', '/ethereum', '/matic', '/CRANNI', '/my-tickets'],
    items: [
      { raw: 'BTC', path: '/' },
      { raw: 'ETH', path: '/ethereum' },
      { raw: 'POL', path: '/matic' },
      { raw: 'CRANNI', path: '/CRANNI' },
      { name: 'nav.myTickets', path: '/my-tickets' },
    ],
  },
  {
    name: 'nav.games',
    isDropdown: true,
    match: ['/prediction', '/bullseye', '/bestiarium', '/referral'],
    items: [
      { name: 'nav.bullVsBear', path: '/prediction' },
      { name: 'nav.bullsEye', path: '/bullseye' },
      { name: 'nav.bestiarium', path: '/bestiarium' },
      { name: 'nav.referral', path: '/referral' },
    ],
  },
  {
    name: 'nav.nftnodes',
    isDropdown: true,
    match: ['/nftnodes'],
    items: [
      { name: 'nav.about', path: '/nftnodes' },
      { name: 'nav.mintBuy', path: '/nftnodes?section=mintbuy' },
      { name: 'nav.dashboard', path: '/nftnodes?section=dashboard' },
      { name: 'nav.governance', path: '/nftnodes?section=governance' },
    ],
  },
  {
    name: 'nav.token',
    isDropdown: true,
    match: ['/token'],
    items: [
      { name: 'nav.overview', path: '/token' },
      { name: 'nav.staking', path: '/token?section=staking' },
      { name: 'nav.cynths', path: '/token?section=cynths' },
    ],
  },
  {
    name: 'nav.docs',
    path: 'https://docs.luckblocks.com',
    isExternal: true,
    isDropdown: false,
  },
];

const dropdownItemClass = (active) =>
  classNames(
    active ? 'bg-white/10 text-white' : 'text-white/75',
    'group flex w-full items-center rounded-lg px-3 py-2 text-sm font-sans tracking-wide transition'
  );

const Header = () => {
  const { t } = useI18n();
  const [isMobileMenu, setMobileMenu] = useState(false);
  const [isScrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Solid bar once the user leaves the top of the page, translucent over the hero.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = isMobileMenu ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenu]);

  useEffect(() => {
    setMobileMenu(false);
  }, [location.pathname, location.search]);

  const closeMobileMenu = () => setMobileMenu(false);

  const isGroupActive = (item) =>
    Array.isArray(item.match) && item.match.includes(location.pathname);

  /** Tickers pass through untouched; everything else goes through the bundle. */
  const label = (entry) => (entry.raw ? entry.raw : t(entry.name));

  return (
    <header
      className={classNames(
        'sticky top-0 z-50 w-full font-sans text-white transition-colors duration-300',
        isScrolled ? 'border-b border-[rgba(var(--lb-gilt-rgb),0.2)]' : 'border-b border-transparent'
      )}
    >
      {/* The bar's fill and blur live on this underlay, not on <header> itself.
          A backdrop-filter makes an element the containing block for its fixed
          descendants, which collapsed the mobile drawer below to the height of
          the header as soon as the page was scrolled. */}
      <div
        aria-hidden="true"
        className={classNames(
          'absolute inset-0 -z-10 transition-colors duration-300',
          isScrolled ? 'bg-black/85 backdrop-blur-xl' : 'bg-black'
        )}
      />

      <div className="mx-auto flex w-full max-w-screen-2xl items-center justify-between gap-4 px-3 py-3 sm:px-6">
        <h1 className="flex shrink-0 items-center">
          <Link to="/" aria-label={t('nav.home')} className="transition-opacity hover:opacity-80">
            {/* Narrower at lg, where the full desktop nav first appears and space is tightest */}
            <img className="w-44 md:w-[270px] lg:w-48 xl:w-[270px]" src={luckblock} alt="Luckblocks" />
          </Link>
        </h1>

        {/* Desktop navigation */}
        <nav className="hidden items-center gap-1 lg:flex">
          {menuItems.map((item) =>
            item.isDropdown ? (
              <Menu as="div" className="relative inline-block text-left" key={item.name}>
                {({ open }) => (
                  <>
                    <Menu.Button
                      className={classNames(
                        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-base font-medium transition xl:px-4',
                        isGroupActive(item) || open
                          ? 'bg-white/10 text-white'
                          : 'text-white/75 hover:bg-white/5 hover:text-white'
                      )}
                    >
                      {label(item)}
                      <ChevronDownIcon
                        className={classNames(
                          'h-4 w-4 transition-transform duration-200',
                          open ? 'rotate-180 text-brand-soft' : 'text-white/50'
                        )}
                        aria-hidden="true"
                      />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-150"
                      enterFrom="transform opacity-0 scale-95 -translate-y-1"
                      enterTo="transform opacity-100 scale-100 translate-y-0"
                      leave="transition ease-in duration-100"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-white/10 bg-[#0f0a14]/95 p-1.5 shadow-2xl shadow-black/60 backdrop-blur-xl focus:outline-none">
                        {item.items.map((subItem) => (
                          <Menu.Item key={subItem.path}>
                            {({ active }) =>
                              subItem.isExternal ? (
                                <a
                                  href={subItem.path}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={dropdownItemClass(active)}
                                >
                                  {label(subItem)}
                                </a>
                              ) : (
                                <Link to={subItem.path} className={dropdownItemClass(active)}>
                                  {label(subItem)}
                                </Link>
                              )
                            }
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            ) : item.isExternal ? (
              <a
                key={item.name}
                href={item.path}
                target="_blank"
                rel="noreferrer"
                className="whitespace-nowrap rounded-full px-3 py-2 text-base font-medium text-white/75 transition hover:bg-white/5 hover:text-white xl:px-4"
              >
                {label(item)}
              </a>
            ) : (
              <Link
                key={item.name}
                to={item.path}
                className="rounded-full px-4 py-2 text-base font-medium text-white/75 transition hover:bg-white/5 hover:text-white"
              >
                {label(item)}
              </Link>
            )
          )}
          <LanguageSwitcher className="ml-2" />

          <div className="ml-3 border-l border-white/10 pl-4">
            <FetchConnectionButton />
          </div>
        </nav>

        {/* Mobile: language stays one tap away in the bar, next to the drawer trigger */}
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            onClick={() => setMobileMenu(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
            aria-label={t('nav.openMenu')}
            aria-expanded={isMobileMenu}
          >
            <MenuIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {/* `invisible` keeps the closed drawer out of the tab order, not just off-screen.
          Transitioning visibility delays the switch until the slide-out has finished.
          `overflow-hidden` clips the closed panel, which is parked one screen-width to
          the right, and a translated element still counts towards the document's scroll
          width, so without this every page scrolls sideways on a phone. */}
      <div
        className={classNames(
          'fixed inset-0 z-50 overflow-hidden transition-[visibility] duration-300 lg:hidden',
          isMobileMenu ? 'visible pointer-events-auto' : 'invisible pointer-events-none'
        )}
      >
        <div
          onClick={closeMobileMenu}
          className={classNames(
            'absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300',
            isMobileMenu ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div
          className={classNames(
            'absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col border-l border-white/10 bg-[#09070d] shadow-2xl transition-transform duration-300 ease-out',
            isMobileMenu ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
            <img className="w-[180px]" src={luckblock} alt="Luckblocks" />
            <button
              onClick={closeMobileMenu}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
              aria-label={t('nav.closeMenu')}
            >
              <CloseIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <nav className="lb-scroll flex-1 space-y-5 overflow-y-auto px-5 py-6">
            {menuItems.map((item) =>
              item.isDropdown ? (
                <div key={item.name}>
                  <p className="lb-label mb-2 px-1">{label(item)}</p>
                  <div className="space-y-1">
                    {item.items.map((subItem) => {
                      const [path, search] = subItem.path.split('?');
                      const isActive =
                        location.pathname === path &&
                        (search ? location.search.includes(search) : !location.search);
                      return subItem.isExternal ? (
                        <a
                          key={subItem.path}
                          href={subItem.path}
                          target="_blank"
                          rel="noreferrer"
                          onClick={closeMobileMenu}
                          className="block rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3 text-base text-white/80 transition hover:bg-white/10 hover:text-white"
                        >
                          {label(subItem)}
                        </a>
                      ) : (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={closeMobileMenu}
                          className={classNames(
                            'block rounded-xl border px-4 py-3 text-base transition',
                            isActive
                              ? 'border-[#b87ff0]/40 bg-brand/15 text-white'
                              : 'border-white/5 bg-white/[0.03] text-white/80 hover:bg-white/10 hover:text-white'
                          )}
                        >
                          {label(subItem)}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <a
                  key={item.name}
                  href={item.path}
                  target="_blank"
                  rel="noreferrer"
                  onClick={closeMobileMenu}
                  className="block rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3 text-base text-white/80 transition hover:bg-white/10 hover:text-white"
                >
                  {label(item)}
                </a>
              )
            )}
          </nav>

          <div className="space-y-4 border-t border-white/10 px-5 py-5">
            <div className="flex justify-center">
              <FetchConnectionButton />
            </div>
            <div className="flex justify-center gap-6">
              <a
                href="https://t.me/luckblockscom"
                target="_blank"
                rel="noreferrer"
                onClick={closeMobileMenu}
                aria-label="Telegram"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition hover:bg-white/10"
              >
                <img src={telegram} alt="" className="h-6" />
              </a>
              <a
                href="https://twitter.com/luckblockscom"
                target="_blank"
                rel="noreferrer"
                onClick={closeMobileMenu}
                aria-label="X / Twitter"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition hover:bg-white/10"
              >
                <img src={twitter} alt="" className="h-6" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
